/* global app */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { getBetgeniusStreamingUrl } from 'integrationActions.js';
import { translation } from 'helpers/utilsHelper.js';
import VideoIcon from 'video_icon.svg';
import S from './StyledGeniusLivePlayer.js';

// ========== UTILITY FUNCTIONS ==========

/**
 * Check if event has Genius Live Stream available
 * @param {Object} eventOrExtras - Event object or extras object
 * @returns {boolean}
 */
export const hasGeniusLiveStream = (eventOrExtras) => {
  try {
    const extras = eventOrExtras?.extras || eventOrExtras;
    if (!extras) return false;
    
    if (eventOrExtras?.providerId && eventOrExtras.providerId !== 2) return false;
    
    const phase = String(extras.BetgeniusStreamingPhase || '').toUpperCase();
    const rawData = extras.BetgeniusStreamingData;
    const hasData = !!rawData && rawData !== '[]' && rawData !== 'null';
    return phase === 'IN_PLAY' && hasData;
  } catch (e) {
    return false;
  }
};

const reloadWidgetScript = (src) => {
  const existingScripts = document.querySelectorAll('script[src*="widgetLoader"]');
  existingScripts.forEach((s) => s.remove());

  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = (e) => reject(new Error(`Failed to load script: ${src}`));
    document.body.appendChild(s);
  });
};

const closePlayerIfExists = () => {
  try {
    if (window.GeniusLivePlayer?.player?.close) {
      window.GeniusLivePlayer.player.close();
    }
    window.GeniusLivePlayer = null;
  } catch (e) {
    /* ignore */
  }
};

/**
 * Generate or retrieve device-specific identifier
 * Stores in localStorage for persistence across sessions
 * @returns {string} Unique device ID
 */
const getOrCreateDeviceId = () => {
  const STORAGE_KEY = 'betgenius_deviceId';
  
  try {
    // Try to get existing deviceId from localStorage
    let deviceId = localStorage.getItem(STORAGE_KEY);
    
    if (deviceId && deviceId.length > 0) {
      return deviceId;
    }
    
    // Generate new deviceId if doesn't exist
    // Format: timestamp-randomUUID
    const timestamp = Date.now();
    let randomPart;
    
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      randomPart = crypto.randomUUID();
    } else {
      // Fallback for browsers without crypto.randomUUID
      randomPart = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }
    
    deviceId = `${timestamp}-${randomPart}`;
    
    // Store in localStorage
    localStorage.setItem(STORAGE_KEY, deviceId);
    
    return deviceId;
  } catch (e) {
    // Fallback if localStorage is not available
    const timestamp = Date.now();
    const randomPart = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    return `${timestamp}-${randomPart}`;
  }
};

// ========== COMPONENT ==========

const GeniusLivePlayer = ({
  // Props from Spike
  event,
  fixtureId,
  extras,
  
  // Display options
  showIcon = false,
  showCloseButton = true,
  
  // Callbacks
  onClose,
  onPlayerStart,
  onError,
  
  // Redux
  dispatch,
  isLogged
}) => {
  const hostRef = useRef(null);
  const handlerRef = useRef(null);
  const mountedRef = useRef(false);
  const isMountedRef = useRef(true);
  
  const [isPlayerVisible, setIsPlayerVisible] = useState(!showIcon);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hostReady, setHostReady] = useState(false);

  const resolvedFixtureId = fixtureId || event?.eventRemoteId;
  const resolvedExtras = extras || event?.extras;
  const streamAvailable = hasGeniusLiveStream(resolvedExtras);

  const mountPlayer = useCallback(async () => {
    if (!hostRef.current) return;
    if (!isMountedRef.current) return;
    
    setIsLoading(true);
    setIsError(false);
    
    if (!isLogged) {
      if (isMountedRef.current) {
        setIsError(true);
        setIsLoading(false);
      }
      return;
    }
    
    closePlayerIfExists();

    if (!streamAvailable) {
      if (isMountedRef.current) {
        setIsError(true);
        setIsLoading(false);
      }
      return;
    }

    const hostId = 'geniusLive';
    hostRef.current.innerHTML = '';
    const existing = document.getElementById(hostId);
    if (existing && existing !== hostRef.current && !hostRef.current.contains(existing)) {
      existing.id = '';
    }
    const playerHost = document.createElement('div');
    playerHost.id = hostId;
    Object.assign(playerHost.style, { 
      width: '100%',
      height: '100%',
      background: '#000', 
      position: 'relative' 
    });

    const checkStreamInterval = setInterval((unusedParam, counterObject) => {
            counterObject.count = (counterObject.count || 0) + 1;

            const container = document.getElementById("video-player-container");
            const iframe = document.getElementById("gs-video-iframe");
            const videoContainer = container && container.lastChild
            const controlBar = videoContainer && videoContainer.lastChild
            const controls = controlBar && controlBar.lastChild

            if (container || iframe) {
                if (container) {
                container.style.setProperty("width", "100%", "important");
                container.style.setProperty("height", "100%", "important");
                }
                if (iframe) {
                iframe.style.setProperty("width", "100%", "important");
                iframe.style.setProperty("height", "100%", "important");
                }
            }
            if(controls && controls.classList.contains('controls')){
                controls.style.setProperty("justify-content", "flex-start");
                controls.style.setProperty("margin-left", "5px");
            }

            if ((container && iframe && controls) || counterObject.count >= 3) {
                clearInterval(checkStreamInterval);
            }
            }, 700, 0, {});

    hostRef.current.appendChild(playerHost);

    const onPlayerReady = async (ev) => {
      try {
        const detail = ev?.detail;
        if (!detail || !isMountedRef.current) return;

        if (detail.type === 'player_not_ready') {
          setIsError(true);
          setIsLoading(false);
          onError?.('player_not_ready');
          return;
        }

        if (detail.type !== 'player_ready') return;

        const body = detail.body;
        if (!body || !body.streamId || !body.deliveryType || !body.deliveryId) {
          setIsError(true);
          setIsLoading(false);
          onError?.('Incomplete player_ready event body');
          return;
        }

        const deviceId = getOrCreateDeviceId();

        const response = await dispatch(getBetgeniusStreamingUrl({
          fixtureId: body.geniusSportsFixtureId,
          liveStreamId: body.streamId,
          deliveryType: body.deliveryType,
          deliveryTypeId: body.deliveryId,
          device: body.device,
          countryCode: body.region,
          deviceId: deviceId
        }));

        const playerData = response?.data || response;

        if (!playerData?.url) {
          setIsError(true);
          setIsLoading(false);
          onError?.('No stream URL in API response');
          return;
        }

        if (isMountedRef.current && window.GeniusLivePlayer?.player?.start) {
          window.GeniusLivePlayer.player.start(playerData);
          onPlayerStart?.();
        }
        
        setIsLoading(false);
      } catch (e) {
        if (isMountedRef.current) {
          setIsError(true);
          setIsLoading(false);
        }
        onError?.(e.message);
      }
    };

    handlerRef.current = onPlayerReady;
    window.addEventListener('geniussportsmessagebus', onPlayerReady);

    const clientId = app.config?.betgeniusClientId;
    const baseUrl = app.config?.betgeniusUrl?.replace(/\/$/, '');
    
    if (!clientId || !baseUrl) {
      if (isMountedRef.current) {
        setIsError(true);
        setIsLoading(false);
      }
      onError?.('Missing betgenius configuration');
      return;
    }
    
    const deviceId = getOrCreateDeviceId();
    const loaderSrc = `${baseUrl}/widgetLoader?customerId=${encodeURIComponent(clientId)}&fixtureId=${encodeURIComponent(resolvedFixtureId)}&deviceId=${encodeURIComponent(deviceId)}&autoplayEnabled=true&audioEnabled=true&_t=${Date.now()}`;

    try {
      await reloadWidgetScript(loaderSrc);
    } catch (e) {
      if (isMountedRef.current) {
        setIsError(true);
        setIsLoading(false);
      }
      onError?.(e.message);
    }
  }, [resolvedFixtureId, streamAvailable, dispatch, isLogged, onError, onPlayerStart]);

  const setHostRef = useCallback((node) => {
    hostRef.current = node;
    if (node) {
      setHostReady(true);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isPlayerVisible && hostReady && hostRef.current && !mountedRef.current) {
      mountedRef.current = true;
      mountPlayer();
    }

    return () => {
      if (handlerRef.current) {
        window.removeEventListener('geniussportsmessagebus', handlerRef.current);
        handlerRef.current = null;
      }
      closePlayerIfExists();
      mountedRef.current = false;
    };
  }, [isPlayerVisible, hostReady, mountPlayer, resolvedFixtureId]);

  const handleIconClick = useCallback(() => {
    if (!isLogged) return;
    if (!streamAvailable) return;
    setIsPlayerVisible(true);
  }, [isLogged, streamAvailable]);

  const handleClose = useCallback(() => {
    if (handlerRef.current) {
      window.removeEventListener('geniussportsmessagebus', handlerRef.current);
    }
    closePlayerIfExists();
    setIsPlayerVisible(false);
    setIsError(false);
    onClose?.();
  }, [onClose]);

  if (!isLogged || !streamAvailable) {
    return null;
  }

  if (showIcon && !isPlayerVisible) {
    return (
      <S.Wrapper>
        <S.StreamIcon 
          onClick={handleIconClick} 
          title={translation('geniusLivePlayer_watchStream') || 'Oglądaj stream na żywo'}
          dangerouslySetInnerHTML={{ __html: VideoIcon }}
        />
      </S.Wrapper>
    );
  }

  if (isError) {
    const errorMessage = translation('geniusLivePlayer_streamUnavailable') || 'Stream niedostępny';
    const closeTitle = translation('close') || 'Zamknij';
    return (
      <S.Wrapper>
        <S.ErrorBar>
          <S.ErrorMessage>{errorMessage}</S.ErrorMessage>
          {showCloseButton && (
            <S.ControlButton onClick={handleClose} title={closeTitle}>
              ✕
            </S.ControlButton>
          )}
        </S.ErrorBar>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      <S.PlayerWrapper>
        <S.PlayerControls>
          {showCloseButton && (
            <S.ControlButton onClick={handleClose} title={translation('close') || 'Zamknij'}>
              ✕
            </S.ControlButton>
          )}
        </S.PlayerControls>
        {isLoading && (
          <S.LoadingOverlay>
            {translation('geniusLivePlayer_loading') || 'Ładowanie streamu...'}
          </S.LoadingOverlay>
        )}
        <S.PlayerHost ref={setHostRef} />
      </S.PlayerWrapper>
    </S.Wrapper>
  );
};

const mapStateToProps = (state) => {
  const { Auth: { isLogged } } = state;
  return {
    isLogged
  };
};

export default connect(mapStateToProps)(GeniusLivePlayer);
